'use client';

// A/B Testing utility for continuous optimization
// Based on audit recommendations for testing different content versions

export interface ABTestVariant {
  id: string;
  name: string;
  weight: number; // Percentage of traffic (0-100)
  active: boolean;
}

export interface ABTest {
  id: string;
  name: string;
  description: string;
  variants: ABTestVariant[];
  startDate: Date;
  endDate?: Date;
  active: boolean;
}

// Current A/B tests configuration
export const activeABTests: ABTest[] = [
  {
    id: 'hero-headline-test',
    name: 'Hero Headline Optimization',
    description: 'Testing different headline variations for conversion optimization',
    variants: [
      { id: 'control', name: 'Current: Stop Losing Leads', weight: 50, active: true },
      { id: 'variant-a', name: 'Alternative: Convert More Visitors', weight: 50, active: true }
    ],
    startDate: new Date(),
    active: true
  },
  {
    id: 'cta-button-test',
    name: 'CTA Button Text Optimization',
    description: 'Testing different CTA button texts for better conversion',
    variants: [
      { id: 'control', name: 'Current: See My Lead Report Now', weight: 50, active: true },
      { id: 'variant-a', name: 'Alternative: Get My Free Analysis', weight: 50, active: true }
    ],
    startDate: new Date(),
    active: true
  }
];

// Get user's assigned variant for a specific test
export function getABTestVariant(testId: string): string {
  if (typeof window === 'undefined') return 'control';
  
  const test = activeABTests.find(t => t.id === testId && t.active);
  if (!test) return 'control';
  
  // Check if user already has an assigned variant (stored in localStorage)
  const storageKey = `ab_test_${testId}`;
  const existingVariant = localStorage.getItem(storageKey);
  
  if (existingVariant) {
    return existingVariant;
  }
  
  // Assign new variant based on weights
  const random = Math.random() * 100;
  let cumulativeWeight = 0;
  
  for (const variant of test.variants) {
    if (!variant.active) continue;
    
    cumulativeWeight += variant.weight;
    if (random <= cumulativeWeight) {
      localStorage.setItem(storageKey, variant.id);
      
      // Track assignment event for analytics
      if (typeof window !== 'undefined' && (window as any).clarity) {
        (window as any).clarity('set', `ab_test_${testId}`, variant.id);
      }
      
      return variant.id;
    }
  }
  
  // Fallback to control
  localStorage.setItem(storageKey, 'control');
  return 'control';
}

// Track A/B test conversion events
export function trackABTestConversion(testId: string, conversionType: string = 'conversion') {
  if (typeof window === 'undefined') return;
  
  const variant = getABTestVariant(testId);
  
  // Track with Microsoft Clarity
  if ((window as any).clarity) {
    (window as any).clarity('event', `ab_conversion_${testId}_${variant}`, {
      test_id: testId,
      variant: variant,
      conversion_type: conversionType,
      timestamp: new Date().toISOString()
    });
  }
  
  // Track with Google Analytics if available
  if ((window as any).gtag) {
    (window as any).gtag('event', 'ab_test_conversion', {
      test_id: testId,
      variant: variant,
      conversion_type: conversionType
    });
  }
}

// Utility to check if A/B testing is enabled
export function isABTestingEnabled(): boolean {
  return process.env.NODE_ENV === 'production' || process.env.NEXT_PUBLIC_ENABLE_AB_TESTING === 'true';
}
